import {
  ArchiveSharp,
  DeleteSharp,
  EditSharp,
  UnarchiveSharp,
  VisibilitySharp,
  WorkOutlineSharp,
} from "@mui/icons-material";
import {
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo } from "react";

import { cn } from "../utils/cn";

type PropsType = {
  showRowNo?: boolean;
  loading: boolean;
  headers?: {
    title: string;
    className?: string;
  }[];
  rows: any[];
  columns: any[];
  totalRecords?: number;
  page?: number;
  pageSize?: number;
  disabledShadow?: boolean;
  onView?: (data: any) => void;
  onEdit?: (data: any) => void;
  onToggleArchive?: (data: any) => void;
  onDelete?: (data: any) => void;
  onPageChange?: (event: unknown, page: number) => void;
  onPageSizeChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  actionPosition?: string;
  keyShowAction?: { key: string; value: string | number | boolean };
  noEntriesRow?: boolean;
};

const CustomTable = ({
  showRowNo = false,
  loading,
  headers,
  rows,
  columns,
  totalRecords,
  page,
  pageSize,
  disabledShadow = false,
  onView,
  onEdit,
  onToggleArchive,
  onDelete,
  onPageChange,
  onPageSizeChange,
  actionPosition = "end",
  keyShowAction,
  noEntriesRow = false,
}: PropsType) => {
  const theme = useTheme();
  const skeletonHeaders = useMemo(() => {
    const headColumns = headers ? headers : columns ? columns : [];
    return headColumns;
  }, [headers, columns]);

  const columnsColspan = useMemo(() => {
    let colspan = headers?.length || columns?.length || 0;
    if (showRowNo) colspan++;
    if (onView || onEdit || onDelete || onToggleArchive) colspan++;
    return colspan;
  }, [headers, columns, showRowNo, onView, onEdit, onDelete, onToggleArchive]);

  const showAction = useMemo(() => {
    if (onView || onEdit || onDelete || onToggleArchive) {
      return true;
    }

    return false;
  }, [onView, onEdit, onDelete, onToggleArchive]);

  const actionWidth = useMemo(() => {
    const width = 80;
    if (!showAction) return `${width}px`;
    const actions = [onView, onEdit, onToggleArchive, onDelete];
    const count = actions.filter(Boolean).length;
    return `${width * count}px`;
  }, [showAction, onView, onEdit, onDelete, onToggleArchive]);

  if (loading) {
    return (
      <div className="overflow-auto grid">
        <Paper
          className="overflow-auto grid"
          sx={{ boxShadow: disabledShadow ? "none" : "var(--Paper-shadow)" }}
        >
          <TableContainer>
            <Table>
              {headers && headers?.length > 0 && (
                <TableHead>
                  <TableRow className="uppercase">
                    {headers.map((header, index) => (
                      <TableCell
                        key={`table-header-cell-${index}`}
                        className={header?.className || ""}
                      >
                        {header?.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {[...Array(5)].map((_, rowIndex) => (
                  <TableRow key={`table-rows-${rowIndex}`}>
                    {skeletonHeaders.map((_, index) => (
                      <TableCell key={`table-rows-cell-${index}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>
    );
  }

  const renderAction = (row: any) => {
    return (
      <TableCell
        className="relative p-0"
        sx={{
          minWidth: actionWidth,
          width: actionWidth,
          maxWidth: actionWidth,
        }}
      >
        <div className="absolute top-0 left-0 flex w-full h-full">
          {onView && (
            <Button
              variant="contained"
              className="w-full h-full bg-gray-100 hover:bg-gray-400 active:bg-gray-400 text-primary rounded-none shadow-none flex flex-col justify-center items-center gap-1"
              onClick={() => onView(row)}
            >
              <VisibilitySharp />
              <Typography
                variant="overline"
                className="text-primary leading-none"
              >
                View
              </Typography>
            </Button>
          )}
          {onEdit &&
            (!keyShowAction ||
              row[keyShowAction.key] === keyShowAction.value ||
              (keyShowAction.value === "NULL" && !row[keyShowAction.key])) && (
              <Button
                variant="contained"
                className="w-full h-full bg-gray-100 hover:bg-gray-400 active:bg-gray-400 text-primary rounded-none shadow-none flex flex-col justify-center items-center gap-1"
                onClick={() => onEdit(row)}
              >
                <EditSharp />
                <Typography
                  variant="overline"
                  className="text-primary leading-none"
                >
                  Edit
                </Typography>
              </Button>
            )}

          {onToggleArchive &&
            (!keyShowAction ||
              row[keyShowAction.key] === keyShowAction.value ||
              (keyShowAction.value === "NULL" && !row[keyShowAction.key])) && (
              <Button
                color={row?.is_active ? "red" : "success"}
                variant="contained"
                className="w-full h-full rounded-none shadow-none flex flex-col justify-center items-center gap-1"
                onClick={() => onToggleArchive(row)}
              >
                {row?.is_active ? <ArchiveSharp /> : <UnarchiveSharp />}
                <Typography
                  variant="overline"
                  className={cn(
                    "leading-none text-white",
                    row?.is_active && "text-error-dark",
                  )}
                >
                  {row?.is_active ? "Archive" : "Unarchive"}
                </Typography>
              </Button>
            )}

          {onDelete &&
            (!keyShowAction ||
              row[keyShowAction.key] === keyShowAction.value ||
              (keyShowAction.value === "NULL" && !row[keyShowAction.key])) && (
              <Button
                color="error"
                variant="contained"
                className="w-full h-full rounded-none shadow-none flex flex-col justify-center items-center gap-1"
                onClick={() => onDelete(row)}
              >
                <DeleteSharp />
                <Typography
                  variant="overline"
                  className="text-white leading-none"
                >
                  Delete
                </Typography>
              </Button>
            )}
        </div>
      </TableCell>
    );
  };

  return (
    <div className="overflow-auto grid">
      <Paper
        className="overflow-auto grid"
        sx={{ boxShadow: disabledShadow ? "none" : "var(--Paper-shadow)" }}
      >
        <TableContainer>
          <Table>
            {headers && headers?.length > 0 && (
              <TableHead>
                <TableRow className="uppercase">
                  {showAction && actionPosition === "start" && (
                    <TableCell
                      className="text-center text-sm font-semibold"
                      sx={{
                        minWidth: actionWidth,
                        width: actionWidth,
                        maxWidth: actionWidth,
                      }}
                    >
                      Action
                    </TableCell>
                  )}
                  {showRowNo && (
                    <TableCell className="text-center text-sm font-semibold">
                      No.
                    </TableCell>
                  )}
                  {headers.map((header, index) => (
                    <TableCell
                      key={`table-header-cell-${index}`}
                      className={header?.className || ""}
                    >
                      {header?.title}
                    </TableCell>
                  ))}
                  {showAction && actionPosition === "end" && (
                    <TableCell
                      className="text-center text-sm font-semibold"
                      sx={{
                        minWidth: actionWidth,
                        width: actionWidth,
                        maxWidth: actionWidth,
                      }}
                    >
                      Action
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {rows && rows?.length > 0 ? (
                <>
                  {rows.map((row, index) => (
                    <TableRow
                      key={`table-row-${index}`}
                      sx={{
                        backgroundColor:
                          row?.is_reject_task === true
                            ? theme.vars.palette.error.light
                            : "",
                      }}
                    >
                      {showAction &&
                        actionPosition === "start" &&
                        renderAction(row)}
                      {showRowNo && (
                        <TableCell className="text-center">
                          {page && pageSize
                            ? index + 1 + page * pageSize
                            : index + 1}
                        </TableCell>
                      )}
                      {columns?.map((column, colInd) => (
                        <TableCell
                          key={`table-row-cell-${colInd}`}
                          className={
                            typeof column.className === "function"
                              ? column.className(row, index)
                              : column.className || ""
                          }
                        >
                          {typeof column === "object" &&
                          typeof column.value === "function"
                            ? column.value(row)
                            : row[column]}
                        </TableCell>
                      ))}
                      {showAction &&
                        actionPosition === "end" &&
                        // <TableCell className="p-0 w-1">
                        //   <div className="flex flex-nowrap justify-center items-center">
                        //     {onEdit && (
                        //       <Button
                        //         variant="contained"
                        //         className="bg-gray-100 hover:bg-gray-400 active:bg-gray-400 text-primary rounded-none shadow-none flex flex-col w-16 h-16 justify-center items-center gap-1"
                        //         onClick={() => onEdit(row.id)}
                        //       >
                        //         <EditSharp />
                        //         <Typography
                        //           variant="overline"
                        //           className="text-primary leading-none"
                        //         >
                        //           Edit
                        //         </Typography>
                        //       </Button>
                        //     )}
                        //     {onDelete && (
                        //       <Button
                        //         color="error"
                        //         variant="contained"
                        //         className="rounded-none shadow-none flex flex-col w-16 h-16 justify-center items-center gap-1"
                        //         onClick={() => onDelete(row.id)}
                        //       >
                        //         <DeleteSharp />
                        //         <Typography
                        //           variant="overline"
                        //           className="text-white leading-none"
                        //         >
                        //           Delete
                        //         </Typography>
                        //       </Button>
                        //     )}
                        //   </div>
                        // </TableCell>
                        renderAction(row)}
                    </TableRow>
                  ))}
                </>
              ) : (
                <>
                  {noEntriesRow ? (
                    <TableRow>
                      <TableCell colSpan={columnsColspan}>
                        <div
                          style={{
                            height: "100%",
                            width: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              color: theme.vars.palette.grey[400],
                            }}
                          >
                            <div>There are no entries in the system.</div>
                            <div>Please add a new one.</div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columnsColspan}
                        sx={{ height: { xs: "calc(50vh)", md: "calc(60vh)" } }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                          }}
                        >
                          <WorkOutlineSharp
                            style={{
                              width: "40px",
                              height: "40px",
                              color: theme.vars.palette.grey[400],
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              color: theme.vars.palette.grey[400],
                            }}
                          >
                            <div>There are no entries in the system.</div>
                            <div>Please add a new one.</div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {rows && rows?.length > 0 && totalRecords && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalRecords || 0}
            rowsPerPage={pageSize || 10}
            page={page || 0}
            onPageChange={onPageChange || (() => {})}
            onRowsPerPageChange={onPageSizeChange || (() => {})}
          />
        )}
      </Paper>
    </div>
  );
};

export default CustomTable;
